/**
 * The dashboard's stat band: ONE card whose cells are separated by gray-100
 * hairlines, 2x2 on mobile and a single row on lg.
 *
 * Extracted from QuickStatsSection (the dashboard home's "Total Issues / Open /
 * Closed / This Week" row) when the Schedule page needed the same band. The
 * alternative was a second copy of the divider matrix, the 10.5px eyebrow and
 * the 650-weight numeral, which is precisely the shape that drifts: the
 * component catalog already shipped four wrong grays by hand-typing values
 * this way (docs/component-catalog.html, recorded in CLAUDE.md).
 *
 * Deliberately dumb. It renders labels and values and knows nothing about
 * issues or schedules; every caller does its own aggregation in a pure module
 * and hands the result here already formatted.
 */
import type { ReactNode } from 'react';

export interface StatBandCell {
  /** Stable key, also used as a test hook via data-stat. */
  key: string;
  /** Small uppercase eyebrow. */
  label: string;
  /** The number itself. Pre-formatted: pass a string for "-" or "12". */
  value: ReactNode;
  /** Optional chip rendered beside the value (trend, status). */
  chip?: ReactNode;
  /**
   * Optional tooltip on the cell. Use it to say what a value MEANS when the
   * number alone could be misread, never to hide information the reader needs.
   */
  title?: string;
}

interface StatBandProps {
  cells: StatBandCell[];
  /** Accessible name for the band. */
  ariaLabel: string;
  id?: string;
}

/**
 * Hairlines between cells, by index, for a 2-col / 4-col responsive grid.
 * Index 0 has no border; the rest gain a left or top rule so the band reads as
 * one card with internal dividers (gray-100, the internal-divider rule) rather
 * than four boxed tiles.
 */
export const STAT_BAND_DIVIDERS = [
  '',
  'border-l border-gray-100',
  'border-t lg:border-t-0 lg:border-l border-gray-100',
  'border-l border-t lg:border-t-0 border-gray-100',
];

export function StatBand({ cells, ariaLabel, id }: StatBandProps) {
  return (
    <section id={id} aria-label={ariaLabel}>
      <dl className="card-sm p-0 overflow-hidden grid grid-cols-2 lg:grid-cols-4">
        {cells.map((cell, i) => (
          <div
            key={cell.key}
            data-stat={cell.key}
            title={cell.title}
            className={`p-4 ${STAT_BAND_DIVIDERS[i] ?? ''}`.trimEnd()}
          >
            {/* Small uppercase eyebrow (artifact stat scale: 10.5px / 600 /
                0.08em), not the title-case card-header .section-label. */}
            <dt className="text-[10px] font-semibold uppercase tracking-[0.08em] text-gray-500">
              {cell.label}
            </dt>
            <dd className="flex items-baseline gap-2 leading-none mt-2">
              <span
                className="text-3xl tracking-[-0.02em] text-gray-900 tabular-nums"
                style={{ fontWeight: 650 }}
              >
                {cell.value}
              </span>
              {cell.chip}
            </dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

export function StatBandSkeleton({ ariaLabel }: { ariaLabel: string }) {
  return (
    <section aria-label={ariaLabel}>
      <div className="card-sm p-0 overflow-hidden grid grid-cols-2 lg:grid-cols-4">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className={`p-4 ${STAT_BAND_DIVIDERS[i]}`.trimEnd()}>
            <div className="h-2.5 rounded w-16 skeleton-shimmer" />
            <div className="h-7 rounded w-10 skeleton-shimmer mt-3" />
          </div>
        ))}
      </div>
    </section>
  );
}
